import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

/**
 * A barra de controles some sozinha e volta com o mouse.
 *
 * Ela flutua POR CIMA do palco: enquanto alguem transmite, um retangulo opaco
 * parado no rodape cobre justamente o pedaco da tela que se veio assistir. Ela
 * reaparece ao primeiro movimento do mouse dentro do palco e some de novo
 * quando o mouse para ou sai.
 *
 * Duas situacoes em que ela NAO some, de proposito:
 *
 * - **sem palco**: com a sala vazia a barra e a unica coisa na tela, e some-la
 *   deixaria a pessoa sem botao nenhum ate adivinhar que precisa mexer o mouse;
 * - **sem mouse**: no celular nao existe "passar por cima". Some-la ali deixaria
 *   quem entrou pelo telefone sem como sair da sala ou se mutar, porque a barra
 *   lateral tambem nao aparece nessa largura.
 */

/** Quanto tempo o mouse pode ficar parado antes de a barra sumir. */
const OCIOSO_MS = 2_500

/** O aparelho tem um ponteiro que passa por cima (mouse, trackpad, caneta)? */
function temHover(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(hover: hover)').matches
}

export function useDockAutoHide(area: RefObject<HTMLElement | null>, ativo: boolean): boolean {
    const [visivel, setVisivel] = useState(true)
    /**
     * Espelho do estado pra decidir sem re-renderizar.
     *
     * `pointermove` dispara dezenas de vezes por segundo; chamar `setVisivel`
     * em cada uma seria pedir ao React um trabalho que quase sempre termina em
     * "continua igual".
     */
    const visivelRef = useRef(true)

    useEffect(() => {
        const elemento = area.current
        if (!ativo || !elemento || !temHover()) {
            visivelRef.current = true
            setVisivel(true)
            return
        }

        let ocioso: ReturnType<typeof setTimeout> | undefined

        const mostrar = () => {
            if (!visivelRef.current) {
                visivelRef.current = true
                setVisivel(true)
            }
            clearTimeout(ocioso)
            ocioso = setTimeout(esconder, OCIOSO_MS)
        }

        const esconder = () => {
            clearTimeout(ocioso)
            if (!visivelRef.current) return
            visivelRef.current = false
            setVisivel(false)
        }

        // Comeca a mostra e some sozinha: quem acabou de entrar precisa ver
        // que os controles existem antes de eles se esconderem.
        mostrar()

        elemento.addEventListener('pointermove', mostrar)
        elemento.addEventListener('pointerdown', mostrar)
        elemento.addEventListener('pointerleave', esconder)

        return () => {
            clearTimeout(ocioso)
            elemento.removeEventListener('pointermove', mostrar)
            elemento.removeEventListener('pointerdown', mostrar)
            elemento.removeEventListener('pointerleave', esconder)
        }
    }, [area, ativo])

    return visivel
}
